import React from 'react';
import {Form, Formik, FormikHelpers} from "formik";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import {Building, BuildingData, BuildingProvider} from "../../../types/Building";
import ngage from "../../../services/ngage";
import IconName from "../../../types/IconName";
import {useOperator} from "../../../context/OperatorContext";
import Alert from "../../../components/Alert";
import Selector from "../../../components/Form/Selector";
import {useQuery} from "react-query";
import {useAuth} from "../../../context/AuthContext";
import {Zone} from "../../../types/Zone";
import {FormBlock, FormSection} from "../../../components/Form/Form";
import NotAuthorised from "../../NotAuthorised";
import FloorListField from "./FloorListField";
import FeatureFlag from "../../../types/FeatureFlag";
import {Item} from 'components/Form/Selector/Selector';
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";

// Helpers ----

// TODO: Make this reusable
function zoneAsItem(zone: Zone): Item {
  return {
    id: zone.id || 0,
    name: zone.name,
    icon: IconName.CUBE_TRANSPARENT
  }
}

function selectZone(zoneId: number | string, {setFieldValue}: FormikHelpers<any>) {
  void setFieldValue('zone_id', Number(zoneId))
}

// Types ----

type BuildingFormProps = {
  building?: Building | null
  onSubmit: (values: BuildingData, helpers: FormikHelpers<BuildingData>) => any
}

// Component ----

export default function BuildingForm(props: BuildingFormProps) {
  const {can, cannot, cannotUse} = useAuth();
  const {termFor} = useOperator();
  const isNgageBuilding = props.building?.provider === BuildingProvider.NGAGE;

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required"),
    zone_id: Yup.number().required("This field is required"),
    floors: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("This field is required"),
        order: Yup.number()
          .required("This field is required")
          .min(0, "Must be a minimum of 0")
          .max(255, "Must be a maximum of 255"),
        height: Yup.number()
          .required("This field is required")
          .min(0, "Must be a minimum of 0")
          .max(255, "Must be a maximum of 255"),
        area: Yup.object().shape({
          coordinates: Yup.array().of(
            Yup.array().of(
              Yup.array().of(Yup.number())
            )
          ).required(`Please draw the ${termFor("floor")} area on the map using the drawing tool in the top left corner`),
        }),
      })
    ),
  })

  const initialValues: BuildingData = {
    name: props.building?.name,
    description: props.building?.description,
    zone_id: props.building?.zone_id,
    footprint: props.building?.footprint,
    floors: props?.building?.floors.map(floor => ({
      id: floor.id,
      name: floor.name,
      order: floor.order,
      height: floor.height,
      area: floor.area
    })) || [],
  }

  const {data: zones, isLoading: zonesLoading} = useQuery(
    ['zones.all', {page: 1}],
    () => ngage.zones.all({page: 1}),
    {enabled: can('zones:read')}
  )

  const zoneItems = zones?.data.map(zoneAsItem) || [];

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit} enableReinitialize={true}>
      {formik => {
        if (cannot('buildings:write') || cannotUse(FeatureFlag.IndoorMapping)) return <NotAuthorised />;

        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">

                {!isNgageBuilding &&
                  <Alert
                    message={`This building is configured for indoor mapping, so some details cannot be changed here.`}/>
                }

                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {termFor('Building')} Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Basic details about the {termFor('building')}.
                      </p>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                      <div className="sm:col-span-3 sm:col-start-1">
                        <div className="mt-1">
                          <Field
                            type="text"
                            label="Name *"
                            name="name"
                            id="name"
                            minLength="2"
                            maxLength="255"
                            autoFocus
                            disabled={!isNgageBuilding}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-6">
                        <Field
                          type="text"
                          label="Description"
                          hint="Optional. 255 chars max."
                          name="description"
                          id="description"
                          autoComplete="off"
                          maxLength="255"
                          disabled={!isNgageBuilding}
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <Selector
                          label={`${termFor('Zone')} *`}
                          isLoading={zonesLoading}
                          items={zoneItems}
                          selectedId={initialValues.zone_id}
                          onChange={zone => selectZone(zone.id, formik)}
                        />

                        {(!!formik.errors.zone_id && !!formik.touched.zone_id) &&
                          <FieldHelp isError>
                            {formik.errors.zone_id}
                          </FieldHelp>
                        }
                      </div>

                      <div className="sm:col-span-6">
                        <FormSection
                          name={termFor('Floors')}
                          description={`Basic details about the ${termFor('building')} ${termFor('floors')}.`}
                          columns={6}
                        >
                          <FormBlock columns={6}>
                            <FloorListField name="floors" readOnly={!isNgageBuilding} building={props.building}/>
                          </FormBlock>
                        </FormSection>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <FormControls formik={formik}/>
            </div>
          </Form>
        )}}
    </Formik>
  );
}